import {
  BUILDING_TYPES,
  CASE_SOURCES,
  type CaseDto,
  type CaseSource,
  type DuplicateCandidateDto,
  formatPhone,
  type MeDto,
  type NipLookupDto,
  normalizeNip,
  type PartyDto,
  quickCaseSchema,
  type SearchResultDto,
  caseGapsShort,
} from '@bqcrm/shared';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Chip,
  Divider,
  Group,
  Modal,
  NumberInput,
  Paper,
  Radio,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertTriangle, IconUserCheck, IconX } from '@tabler/icons-react';
import { NewCaseDuplicates } from './PossibleDuplicates';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorText, HttpError } from '../api';
import { usePostalCity } from '../postalCity';
import { can } from '../auth';
import { useCompanies, useOwnerCandidates, useTrades } from '../data';
import { formatPostalCode } from '../format';
import { notifyOk } from '../notify';
import { TradeBadge } from './Badges';
import { fillFromRegistry, NipInput, NipLookupInfo, useNipLookup } from './NipLookup';

interface Chosen {
  id: string;
  displayName: string;
  sites: { id: string; address: string }[];
}

function CandidateList({ items, onPick }: { items: DuplicateCandidateDto[]; onPick: (c: DuplicateCandidateDto) => void }) {
  return (
    <Stack gap={6}>
      {items.map((c) => (
        <Paper key={c.id} withBorder p="xs" radius="sm">
          <Group justify="space-between" wrap="nowrap">
            <div>
              <Text size="sm" fw={600}>
                {c.displayName} {c.city && <Text span c="dimmed" size="xs">· {c.city}</Text>}
              </Text>
              <Text size="xs" c="dimmed">
                {[...c.phones.map(formatPhone), ...c.emails].join(' · ') || 'brak danych kontaktowych'}
              </Text>
              <Group gap={4} mt={2}>
                {c.reasons.map((r) => (
                  <Badge key={r} size="xs" color={c.strong ? 'red' : 'yellow'} variant="light">
                    {r}
                  </Badge>
                ))}
              </Group>
            </div>
            <Button size="xs" variant="light" leftSection={<IconUserCheck size={14} />} onClick={() => onPick(c)}>
              To ten klient
            </Button>
          </Group>
        </Paper>
      ))}
    </Stack>
  );
}

/** pozycja „Kolejka — do przejęcia” w polu Opiekun (QA P17) */
const QUEUE = 'kolejka';

export function QuickCaseModal({
  opened,
  onClose,
  me,
  presetClient,
}: {
  opened: boolean;
  onClose: () => void;
  me: MeDto;
  presetClient?: PartyDto;
}) {
  const trades = useTrades();
  const companies = useCompanies();
  const ownerCandidates = useOwnerCandidates();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canChangeOwner = can(me, 'sprawy.zmiana_opiekuna');

  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<Chosen | null>(null);
  const [kind, setKind] = useState<'osoba' | 'firma'>('osoba');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [name, setName] = useState('');
  const [nip, setNip] = useState('');
  const nipQ = useNipLookup(nip, kind === 'firma');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [siteChoice, setSiteChoice] = useState<string>('new');
  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const cityHint = usePostalCity(postalCode, city, setCity);
  const [buildingType, setBuildingType] = useState<string | null>('dom_jednorodzinny');
  const [usableArea, setUsableArea] = useState<number | string>('');
  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [companyByTrade, setCompanyByTrade] = useState<Record<string, string>>({});
  const [description, setDescription] = useState('');
  const [source, setSource] = useState<CaseSource>('telefon');
  const [urgent, setUrgent] = useState(false);
  // bez prawa przejmowania spraw zakładający nie zostaje opiekunem — domyślnie kolejka
  const selfOwner = can(me, 'sprawy.przejmowanie');
  const [ownerId, setOwnerId] = useState<string | null>(selfOwner ? me.id : QUEUE);
  const [otherContact, setOtherContact] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [conflict, setConflict] = useState<DuplicateCandidateDto[] | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setSearch('');
    setChosen(null);
    setKind('osoba');
    setFirstName('');
    setLastName('');
    setName('');
    setNip('');
    setPhone('');
    setEmail('');
    setSiteChoice('new');
    setStreet('');
    setPostalCode('');
    setCity('');
    setBuildingType('dom_jednorodzinny');
    setUsableArea('');
    setSelectedTrades([]);
    setCompanyByTrade({});
    setDescription('');
    setSource('telefon');
    setUrgent(false);
    setOwnerId(me.id);
    setOtherContact(false);
    setContactName('');
    setContactPhone('');
    setContactRole('');
    setErrors([]);
    setConflict(null);
  };

  useEffect(() => {
    if (opened && presetClient) {
      setChosen({ id: presetClient.id, displayName: presetClient.displayName, sites: presetClient.sites.map((s) => ({ id: s.id, address: s.address })) });
      setSiteChoice(presetClient.sites[0]?.id ?? 'new');
    }
  }, [opened, presetClient]);

  // dane firmy z rejestrów po NIP: puste pola uzupełniane od razu, wypełnione — przyciskiem „Wstaw dane z rejestru”
  const applyNip = (d: NipLookupDto, force = false) => {
    const next = fillFromRegistry({ name, street, postalCode, city }, d, force);
    setName(next.name);
    setStreet(next.street);
    setPostalCode(next.postalCode);
    setCity(next.city);
  };
  useEffect(() => {
    if (nipQ.data && nipQ.data.nip === normalizeNip(nip)) applyNip(nipQ.data);
  }, [nipQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // wyszukiwanie istniejącego klienta (telefon, nazwisko, adres)
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const searchQ = useQuery({
    queryKey: ['quick-search', debouncedSearch],
    queryFn: () => api<SearchResultDto>(`/search?q=${encodeURIComponent(debouncedSearch)}`),
    enabled: !chosen && debouncedSearch.trim().length >= 3,
  });

  // podpowiedź duplikatów dla wpisywanego nowego klienta
  const displayName = kind === 'osoba' ? `${firstName} ${lastName}`.trim() : name.trim();
  // QA P20: miękkie ostrzeżenie o brakach (zapis możliwy) — e-mail nowego klienta, ulica i kod nowego adresu
  const newSite = !chosen || siteChoice === 'new';
  const gaps = caseGapsShort({ ...(chosen ? {} : { emails: [email] }), ...(newSite ? { street, postalCode } : {}) });
  const showGaps = gaps.length > 0 && ((!chosen && !!displayName) || (newSite && !!city.trim()));
  // osoba: do podpowiedzi duplikatów tylko nazwisko (bez imienia)
  const [dupKey] = useDebouncedValue(JSON.stringify({ phone, email, displayName, lastName: kind === 'osoba' ? lastName.trim() : '', street, city, nip: kind === 'firma' ? normalizeNip(nip) : '' }), 400);
  const dupQ = useQuery({
    queryKey: ['quick-dups', dupKey],
    queryFn: () => {
      const p = JSON.parse(dupKey) as { phone: string; email: string; displayName: string; lastName: string; street: string; city: string; nip: string };
      const qs = new URLSearchParams();
      if (p.nip.length === 10) qs.set('nip', p.nip);
      if (p.phone.replace(/\D/g, '').length >= 9) qs.set('phone', p.phone);
      if (p.email.includes('@')) qs.set('email', p.email);
      if (p.lastName.length >= 2) qs.set('lastName', p.lastName);
      else if (!p.lastName && p.displayName.length >= 3 && kind === 'firma') qs.set('name', p.displayName);
      if (p.street.trim()) qs.set('street', p.street);
      if (p.city) qs.set('city', p.city);
      return qs.size ? api<DuplicateCandidateDto[]>(`/parties/duplicates?${qs}`) : Promise.resolve([]);
    },
    enabled: !chosen,
  });

  const pick = async (id: string) => {
    try {
      const p = await api<PartyDto>(`/parties/${id}`);
      setChosen({ id: p.id, displayName: p.displayName, sites: p.sites.map((s) => ({ id: s.id, address: s.address })) });
      setSiteChoice(p.sites[0]?.id ?? 'new');
      setConflict(null);
    } catch (e) {
      setErrors([errorText(e)]);
    }
  };

  const toggleTrade = (codes: string[]) => {
    setSelectedTrades(codes);
    setCompanyByTrade((prev) => {
      const next = { ...prev };
      for (const code of codes) {
        if (!next[code]) next[code] = trades.data?.find((t) => t.code === code)?.defaultCompanyId ?? companies.data?.[0]?.id ?? '';
      }
      return next;
    });
  };

  const companyOptions = useMemo(() => (companies.data ?? []).map((c) => ({ value: c.id, label: `${c.code} · ${c.shortName}` })), [companies.data]);

  const submit = async (force = false) => {
    setErrors([]);
    const site =
      chosen && siteChoice !== 'new'
        ? { id: siteChoice }
        : {
            site: {
              street,
              postalCode,
              city,
              buildingType,
              usableArea: usableArea === '' ? null : Number(usableArea),
              accessNotes: '',
              technicalNotes: '',
              lat: null,
              lng: null,
            },
          };
    const client = chosen
      ? { id: chosen.id }
      : {
          party: {
            kind,
            firstName,
            lastName,
            name,
            nip: kind === 'firma' ? nip : '',
            street,
            postalCode,
            city,
            phones: phone ? [phone] : [],
            emails: email ? [email] : [],
            consentReminders: true,
            consentMarketing: false,
            notes: '',
          },
        };
    const body = {
      client,
      site,
      scopes: selectedTrades.map((tradeCode) => ({ tradeCode, companyId: companyByTrade[tradeCode] })),
      description,
      source,
      priority: urgent ? 'pilny' : 'normalny',
      ownerId: canChangeOwner && ownerId !== QUEUE ? ownerId : null,
      toQueue: ownerId === QUEUE,
      onsiteContact: otherContact ? { fullName: contactName, role: contactRole, phone: contactPhone, email: '', notes: '' } : null,
      force,
    };
    const parsed = quickCaseSchema.safeParse(body);
    if (!parsed.success) {
      setErrors([...new Set(parsed.error.issues.map((i) => i.message))]);
      return;
    }
    setBusy(true);
    try {
      const created = await api<CaseDto>('/cases/quick', { method: 'POST', body: parsed.data });
      // QA7 P11: przyjęcie zatwierdzone od razu (sprawa dla siebie) — sprawa ma bieg, kolejny krok to oferta
      notifyOk(created.status === 'oferta' ? `Dodano sprawę ${created.number} — bieg nadany, następny krok: przygotuj ofertę` : `Dodano zapytanie ${created.number}`);
      void qc.invalidateQueries({ queryKey: ['cases'] });
      void qc.invalidateQueries({ queryKey: ['party'] });
      reset();
      onClose();
      navigate(`/sprawy/${created.id}`);
    } catch (e) {
      if (e instanceof HttpError && e.status === 409 && (e.data as { candidates?: DuplicateCandidateDto[] })?.candidates) {
        setConflict((e.data as { candidates: DuplicateCandidateDto[] }).candidates);
      } else {
        setErrors([errorText(e)]);
      }
    } finally {
      setBusy(false);
    }
  };

  const searchHits = searchQ.data?.parties ?? [];
  const dups = (dupQ.data ?? []).filter((d) => d.strong || d.reasons.length);

  return (
    <Modal
      opened={opened}
      onClose={() => {
        onClose();
      }}
      title="Nowe zapytanie"
      size="xl"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {/* KLIENT */}
        <div>
          <Text fw={700} size="sm" mb={6}>
            Klient
          </Text>
          {chosen ? (
            <Paper withBorder p="sm" bg="var(--app-tint-indigo)">
              <Group justify="space-between">
                <Group gap="xs">
                  <IconUserCheck size={18} />
                  <Text fw={600}>{chosen.displayName}</Text>
                  <Badge size="xs" variant="light">
                    istniejący klient
                  </Badge>
                </Group>
                {!presetClient && (
                  <Button size="xs" variant="subtle" leftSection={<IconX size={14} />} onClick={() => setChosen(null)}>
                    Zmień
                  </Button>
                )}
              </Group>
            </Paper>
          ) : (
            <Stack gap="xs">
              <TextInput
                placeholder="Szukaj w bazie: telefon, nazwisko, nazwa firmy, adres…"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                autoFocus
              />
              {searchHits.length > 0 && (
                <Paper withBorder p="xs">
                  <Text size="xs" c="dimmed" mb={4}>
                    Znalezieni w bazie:
                  </Text>
                  {searchHits.map((p) => (
                    <Group key={p.id} justify="space-between" py={2}>
                      <Text size="sm">
                        {p.displayName} <Text span c="dimmed" size="xs">{[p.city, ...p.phones.map(formatPhone)].filter(Boolean).join(' · ')}</Text>
                      </Text>
                      <Button size="compact-xs" variant="light" onClick={() => void pick(p.id)}>
                        Wybierz
                      </Button>
                    </Group>
                  ))}
                </Paper>
              )}
              <Divider label="albo nowy klient" labelPosition="center" />
              <SegmentedControl
                size="xs"
                value={kind}
                onChange={(v) => setKind(v as 'osoba' | 'firma')}
                data={[
                  { value: 'osoba', label: 'Osoba prywatna' },
                  { value: 'firma', label: 'Firma' },
                ]}
                w="fit-content"
              />
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {kind === 'osoba' ? (
                  <>
                    <TextInput label="Imię" value={firstName} onChange={(e) => setFirstName(e.currentTarget.value)} />
                    <TextInput label="Nazwisko" required value={lastName} onChange={(e) => setLastName(e.currentTarget.value)} />
                  </>
                ) : (
                  <>
                    <NipInput value={nip} onChange={setNip} loading={nipQ.isFetching} />
                    <TextInput label="Nazwa firmy" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
                    <div style={{ gridColumn: '1 / -1' }}>
                      <NipLookupInfo lookup={nipQ} onApply={(d) => applyNip(d, true)} />
                    </div>
                  </>
                )}
                <TextInput label="Telefon" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} />
                <TextInput label="E-mail" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
              </SimpleGrid>
              {dups.length > 0 && (
                <Alert color={dups.some((d) => d.strong) ? 'red' : 'yellow'} icon={<IconAlertTriangle />} title="Czy to ktoś z bazy?" p="xs">
                  <CandidateList items={dups} onPick={(c) => void pick(c.id)} />
                </Alert>
              )}
            </Stack>
          )}
        </div>

        {/* LOKALIZACJA */}
        <div>
          <Text fw={700} size="sm" mb={6}>
            Lokalizacja (obiekt)
          </Text>
          {chosen && chosen.sites.length > 0 && (
            <Radio.Group value={siteChoice} onChange={setSiteChoice} mb="xs">
              <Stack gap={4}>
                {chosen.sites.map((s) => (
                  <Radio key={s.id} value={s.id} label={s.address} />
                ))}
                <Radio value="new" label="Nowy adres" />
              </Stack>
            </Radio.Group>
          )}
          {(!chosen || siteChoice === 'new') && (
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <TextInput label="Ulica i numer" value={street} onChange={(e) => setStreet(e.currentTarget.value)} style={{ gridColumn: 'span 2' }} />
              <TextInput label="Kod pocztowy" placeholder="00-000" inputMode="numeric" maxLength={6} value={postalCode} onChange={(e) => setPostalCode(formatPostalCode(e.currentTarget.value))} />
              <TextInput label="Miejscowość" required value={city} description={cityHint} onChange={(e) => setCity(e.currentTarget.value)} />
              <Select
                label="Typ budynku"
                data={Object.entries(BUILDING_TYPES).map(([value, label]) => ({ value, label }))}
                value={buildingType}
                onChange={setBuildingType}
                clearable
              />
              <NumberInput label="Powierzchnia użytkowa (m²)" value={usableArea} onChange={setUsableArea} min={1} hideControls />
            </SimpleGrid>
          )}
        </div>

        {/* ZAKRES */}
        <div>
          <Text fw={700} size="sm" mb={6}>
            Czego dotyczy zapytanie
          </Text>
          <Chip.Group multiple value={selectedTrades} onChange={toggleTrade}>
            <Group gap={6}>
              {(trades.data ?? [])
                .filter((t) => t.active)
                .map((t) => (
                  <Chip key={t.code} value={t.code} size="sm">
                    {t.name}
                  </Chip>
                ))}
            </Group>
          </Chip.Group>
          {selectedTrades.length > 0 && (
            <Stack gap={6} mt="sm">
              {selectedTrades.map((code) => (
                <Group key={code} gap="sm" wrap="wrap">
                  <TradeBadge code={code} />
                  <Text size="sm" w={{ base: 'auto', sm: 260 }} truncate>
                    {trades.data?.find((t) => t.code === code)?.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    realizuje i rozlicza:
                  </Text>
                  <Select
                    size="xs"
                    data={companyOptions}
                    value={companyByTrade[code] ?? null}
                    onChange={(v) => v && setCompanyByTrade((p) => ({ ...p, [code]: v }))}
                    allowDeselect={false}
                    w={{ base: '100%', sm: 260 }}
                  />
                </Group>
              ))}
            </Stack>
          )}
        </div>

        <Textarea label="Opis potrzeby" autosize minRows={2} value={description} onChange={(e) => setDescription(e.currentTarget.value)} />

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Select
            label="Źródło"
            data={Object.entries(CASE_SOURCES).map(([value, label]) => ({ value, label }))}
            value={source}
            onChange={(v) => v && setSource(v as CaseSource)}
            allowDeselect={false}
          />
          <Select
            label="Opiekun"
            description={ownerId === QUEUE ? 'Przejmie ją opiekun z kolejki i zrobi weryfikację wstępną' : undefined}
            data={[
              ...(canChangeOwner ? (ownerCandidates.data ?? []).map((u) => ({ value: u.id, label: u.fullName })) : []),
              ...(selfOwner && !(canChangeOwner && ownerCandidates.data?.some((u) => u.id === me.id)) ? [{ value: me.id, label: me.fullName }] : []),
              { value: QUEUE, label: 'Kolejka — do przejęcia' },
            ]}
            value={ownerId}
            onChange={setOwnerId}
            allowDeselect={false}
          />
        </SimpleGrid>

        <Group>
          <Switch label="Pilne" checked={urgent} onChange={(e) => setUrgent(e.currentTarget.checked)} />
          <Checkbox label="Kontakt na miejscu to inna osoba niż klient" checked={otherContact} onChange={(e) => setOtherContact(e.currentTarget.checked)} />
        </Group>
        {otherContact && (
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <TextInput label="Imię i nazwisko" value={contactName} onChange={(e) => setContactName(e.currentTarget.value)} />
            <TextInput label="Telefon" value={contactPhone} onChange={(e) => setContactPhone(e.currentTarget.value)} />
            <TextInput label="Kim jest (np. żona, kierownik budowy)" value={contactRole} onChange={(e) => setContactRole(e.currentTarget.value)} />
          </SimpleGrid>
        )}

        {/* P8: otwarte sprawy wybranego klienta lub adresu w wybranych branżach */}
        {chosen && <NewCaseDuplicates clientId={chosen.id} siteId={siteChoice === 'new' ? null : siteChoice} street={street} city={city} trades={selectedTrades} />}
        {showGaps && (
          <Alert color="yellow" variant="light" icon={<IconAlertTriangle />} p="xs">
            <Text size="sm">Brakuje: {gaps.join(', ')}. Zapytanie możesz zapisać — braki zobaczy opiekun przy przyjęciu sprawy.</Text>
          </Alert>
        )}
        {conflict && (
          <Alert color="red" icon={<IconAlertTriangle />} title="W bazie jest już kontrahent z tymi danymi kontaktowymi">
            <CandidateList items={conflict} onPick={(c) => void pick(c.id)} />
            <Button mt="sm" size="xs" color="red" variant="outline" onClick={() => void submit(true)} loading={busy}>
              To inna osoba — zapisz nowego klienta
            </Button>
          </Alert>
        )}
        {errors.length > 0 && (
          <Alert color="red" style={{ whiteSpace: 'pre-line' }}>
            {errors.join('\n')}
          </Alert>
        )}

        <Group justify="space-between">
          <Button variant="subtle" color="gray" onClick={reset}>
            Wyczyść
          </Button>
          <Group>
            <Button variant="default" onClick={onClose}>
              Anuluj
            </Button>
            <Button onClick={() => void submit(false)} loading={busy}>
              Dodaj zapytanie
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
